// Processes a visitor (shared link, no account) started on this device, per link token, so that
// opening the link again (or scanning the QR code again) can continue instead of starting over.
// Stored in localStorage; every access is guarded (private mode, blocked storage).

export interface IVisitorProcess {
  templateId: number
  processId: number
  processToken: string
  startedAt: string
}

const KEY = 'alphaprocess.visitorProcesses.v1'
// older entries are not offered anymore (the process is overdue or cleaned up by then)
const MAX_AGE_MS = 14 * 24 * 3600 * 1000

function readAll(): Record<string, IVisitorProcess> {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function writeAll(all: Record<string, IVisitorProcess>) {
  try {
    localStorage.setItem(KEY, JSON.stringify(all))
  } catch {
    // storage blocked: the visitor simply starts a new process next time
  }
}

export function rememberVisitorProcess(linkToken: string, entry: IVisitorProcess) {
  writeAll({ ...readAll(), [linkToken]: entry })
}

export function findVisitorProcess(linkToken: string, templateId: number): IVisitorProcess | null {
  const entry = readAll()[linkToken]
  if (!entry || entry.templateId !== templateId) return null
  if (Date.now() - new Date(entry.startedAt).getTime() > MAX_AGE_MS) {
    forgetVisitorProcess(linkToken)
    return null
  }
  return entry
}

export function forgetVisitorProcess(linkToken: string) {
  const all = readAll()
  delete all[linkToken]
  writeAll(all)
}

// the process was closed (or is gone): no link offers it anymore
export function forgetVisitorProcessByToken(processToken: string) {
  const all = readAll()
  Object.keys(all).filter((k) => all[k].processToken === processToken).forEach((k) => delete all[k])
  writeAll(all)
}
